<template>
  <div>
    <!-- Hero -->
    <section class="relative bg-hero-fade pt-20 pb-14 lg:pt-28">
      <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>
      <div class="relative container-wide max-w-4xl text-center">
        <div class="eyebrow mx-auto w-fit mb-4">Platform</div>
        <h1 class="font-display text-5xl lg:text-6xl text-ink-900 leading-[1.05] text-balance">
          Find leads. Fix SEO. <span class="italic text-brand-700">Close deals.</span>
        </h1>
        <p class="mt-6 text-lg text-ink-600 max-w-2xl mx-auto text-pretty">
          The only platform that combines warm lead discovery with deep SEO + GEO analysis — so you stop hunting and start winning.
        </p>
      </div>
    </section>

    <!-- Feature sections -->
    <section class="py-20 lg:py-28">
      <div class="container-wide max-w-6xl space-y-24">
        <div
          v-for="(group, i) in groups"
          :key="group.label"
          :id="group.slug"
          class="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start scroll-mt-20"
          :class="i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''"
        >
          <!-- Title column -->
          <div class="lg:col-span-5">
            <div class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-700 bg-brand-50 px-3 py-1 rounded-full border border-brand-100 mb-4">
              <Icon :name="group.icon" :size="12" /> {{ group.label }}
            </div>
            <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">
              {{ group.title }}
            </h2>
            <p class="mt-4 text-ink-600 text-lg leading-relaxed">{{ group.description }}</p>
            <router-link
              v-if="group.linkTo"
              :to="group.linkTo"
              class="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors"
            >
              {{ group.linkLabel }}
              <Icon name="arrow-right" :size="14" />
            </router-link>
          </div>

          <!-- Feature grid column -->
          <div class="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              v-for="f in group.features"
              :key="f.title"
              :id="f.slug"
              class="card card-hover p-5 group scroll-mt-20"
            >
              <span class="w-10 h-10 rounded-lg bg-ink-50 text-ink-800 flex items-center justify-center mb-4 group-hover:bg-brand-50 group-hover:text-brand-700 transition-colors">
                <Icon :name="f.icon" :size="16" />
              </span>
              <h3 class="text-sm font-semibold text-ink-900 tracking-tight">{{ f.title }}</h3>
              <p class="mt-1.5 text-xs text-ink-600 leading-relaxed">{{ f.body }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Depth of Analysis: Basic vs Advanced + competitive edge -->
    <section class="py-24 bg-ink-50/60 border-y border-ink-100">
      <div class="container-wide max-w-6xl">
        <div class="max-w-2xl mb-12">
          <div class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-700 bg-brand-50 px-3 py-1 rounded-full border border-brand-100 mb-4">
            <Icon name="layers" :size="12" /> Depth
          </div>
          <h2 class="font-display text-4xl text-ink-900 leading-tight text-balance">The depth of our SEO analysis.</h2>
          <p class="mt-4 text-ink-600 text-lg leading-relaxed">
            Every plan gets a real, comprehensive audit — not a stripped-down demo. Advanced tiers add the
            competitive and AI-readiness layers that turn a report into a strategy.
          </p>
        </div>

        <!-- Basic vs Advanced comparison -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div class="card p-7">
            <div class="flex items-center gap-2 mb-1">
              <span class="w-8 h-8 rounded-lg bg-ink-100 text-ink-700 flex items-center justify-center shrink-0">
                <Icon name="check-circle" :size="16" />
              </span>
              <h3 class="font-semibold text-lg text-ink-900">Included on every plan</h3>
            </div>
            <p class="text-xs text-ink-500 mb-4 ml-10">Even the free tier gets a real audit, not a teaser.</p>
            <ul class="space-y-2.5">
              <li v-for="item in basicAnalysis" :key="item" class="flex items-start gap-2 text-sm">
                <Icon name="check-circle" :size="14" classes="text-accent-600 mt-0.5 shrink-0" />
                <span class="text-ink-700">{{ item }}</span>
              </li>
            </ul>
          </div>

          <div class="card p-7 border-2 border-brand-500">
            <div class="flex items-center gap-2 mb-1">
              <span class="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                <Icon name="sparkles" :size="16" />
              </span>
              <h3 class="font-semibold text-lg text-ink-900">Unlocked on Pro and above</h3>
            </div>
            <p class="text-xs text-ink-500 mb-4 ml-10">The competitive intelligence and AI-readiness layer.</p>
            <ul class="space-y-2.5">
              <li v-for="item in advancedAnalysis" :key="item" class="flex items-start gap-2 text-sm">
                <Icon name="sparkles" :size="14" classes="text-brand-600 mt-0.5 shrink-0" />
                <span class="text-ink-700">{{ item }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- White Label + SMTP cross-reference (full detail lives in the section below) -->
        <div class="mt-5 card p-5 flex flex-col sm:flex-row sm:items-center gap-3 bg-white">
          <span class="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
            <Icon name="brush" :size="18" />
          </span>
          <p class="text-sm text-ink-600 flex-1">
            Every report — Basic or Advanced — can be fully <strong class="text-ink-900">white-labeled</strong> with your
            own domain and branding, and sent straight from your own <strong class="text-ink-900">SMTP</strong> domain
            (configured once under Settings → White Label → SMTP). Your clients only ever see your agency.
          </p>
          <a href="#white-label" class="btn-outline btn-sm shrink-0">See how <Icon name="arrow-right" :size="12" /></a>
        </div>

        <!-- Competitive edge -->
        <div class="mt-14">
          <div class="max-w-2xl mb-8">
            <h3 class="font-display text-2xl text-ink-900 leading-tight">Warm leads, not just a list.</h3>
            <p class="mt-3 text-ink-600 leading-relaxed">
              Generic directories, manual Google searches, and spreadsheet-based prospecting hand you names — not need.
              LeadRanks scores every prospect against the same audit engine your clients pay for, so you know who's
              actually losing traffic before you ever send an email.
            </p>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div v-for="c in competitiveEdge" :key="c.title" class="card p-5">
              <span class="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mb-3">
                <Icon :name="c.icon" :size="16" />
              </span>
              <h4 class="text-sm font-semibold text-ink-900 tracking-tight">{{ c.title }}</h4>
              <p class="mt-1.5 text-xs text-ink-600 leading-relaxed">{{ c.body }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- White Label spotlight -->
    <section id="white-label" class="py-24 bg-ink-900 text-white border-y border-ink-800 relative overflow-hidden">
      <div class="absolute -top-32 -left-32 w-96 h-96 bg-brand-500/15 rounded-full blur-3xl"></div>
      <div class="absolute -bottom-32 -right-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div class="container-wide max-w-5xl relative">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-400 bg-brand-900/50 px-3 py-1 rounded-full border border-brand-800 mb-4">
              <Icon name="brush" :size="12" /> White Label
            </div>
            <h2 class="font-display text-4xl leading-tight text-white">Your brand. Your reports. Your clients never know we exist.</h2>
            <p class="mt-4 text-lg text-ink-300 leading-relaxed">
              Replace every LeadRanks logo with yours. Custom domain, brand colors, company name — the entire platform wears your identity. Your clients see <em>your</em> agency delivering world-class SEO intelligence.
            </p>
            <ul class="mt-5 space-y-2 text-sm text-ink-300">
              <li class="flex items-start gap-2"><Icon name="check-circle" :size="16" class="text-brand-400 mt-0.5 shrink-0" /> Custom logo, favicon, and brand colors</li>
              <li class="flex items-start gap-2"><Icon name="check-circle" :size="16" class="text-brand-400 mt-0.5 shrink-0" /> White-labeled report URLs and PDFs</li>
              <li class="flex items-start gap-2"><Icon name="check-circle" :size="16" class="text-brand-400 mt-0.5 shrink-0" /> Email reports from your own domain</li>
              <li class="flex items-start gap-2"><Icon name="check-circle" :size="16" class="text-brand-400 mt-0.5 shrink-0" /> Your clients never see "LeadRanks" anywhere</li>
            </ul>
            <router-link to="/pricing" class="btn-brand btn-lg mt-6 inline-flex items-center gap-2">
              See plans with White Label <Icon name="arrow-right" :size="16" />
            </router-link>
          </div>
          <div class="card bg-ink-800/60 border border-ink-700 p-6 space-y-4">
            <div class="flex items-center gap-3 pb-3 border-b border-ink-700">
              <div class="w-3 h-3 rounded-full bg-danger-500"></div>
              <div class="w-3 h-3 rounded-full bg-warn-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
              <span class="ml-auto text-xs text-ink-500 font-mono">acme-agency.com/report/...</span>
            </div>
            <div class="space-y-3">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-md bg-brand-600 flex items-center justify-center text-xs font-bold">A</div>
                <div>
                  <div class="text-sm font-semibold">Acme Digital</div>
                  <div class="text-xs text-ink-400">SEO Report · client-site.com</div>
                </div>
                <span class="ml-auto text-2xl font-extrabold text-brand-400">87</span>
              </div>
              <div class="h-2 bg-ink-700 rounded-full overflow-hidden">
                <div class="h-full bg-gradient-to-r from-brand-500 to-green-400 rounded-full" style="width:87%"></div>
              </div>
              <div class="grid grid-cols-3 gap-2 text-center text-xs">
                <div class="bg-ink-700/50 rounded-lg p-2"><div class="text-ink-400">Technical</div><div class="font-bold text-white">91%</div></div>
                <div class="bg-ink-700/50 rounded-lg p-2"><div class="text-ink-400">Content</div><div class="font-bold text-white">82%</div></div>
                <div class="bg-ink-700/50 rounded-lg p-2"><div class="text-ink-400">Authority</div><div class="font-bold text-white">74%</div></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Export & Act section -->
    <section class="py-24 bg-ink-50/60 border-y border-ink-100">
      <div class="container-wide max-w-5xl">
        <div class="text-center max-w-xl mx-auto mb-10">
          <div class="eyebrow mx-auto w-fit mb-3">Export &amp; Act</div>
          <h2 class="font-display text-3xl text-ink-900">From insight to outreach in minutes.</h2>
          <p class="mt-3 text-sm text-ink-600">
            Don't let leads sit in a dashboard. Export, import, and start conversations while the iron is hot.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div class="card p-6 text-center group">
            <span class="w-12 h-12 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Icon name="download" :size="22" />
            </span>
            <h3 class="font-semibold text-ink-900">CSV Export</h3>
            <p class="mt-2 text-sm text-ink-600">Export warm leads with one click. Domain, contact, SEO issues, score — all in a clean CSV ready to go.</p>
          </div>
          <div class="card p-6 text-center group">
            <span class="w-12 h-12 rounded-xl bg-accent-50 text-accent-700 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Icon name="external" :size="22" />
            </span>
            <h3 class="font-semibold text-ink-900">Import to Instantly</h3>
            <p class="mt-2 text-sm text-ink-600">Drop your CSV straight into Instantly, Lemlist, or any cold outreach tool. No manual data entry.</p>
          </div>
          <div class="card p-6 text-center group">
            <span class="w-12 h-12 rounded-xl bg-green-50 text-green-700 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Icon name="mail" :size="22" />
            </span>
            <h3 class="font-semibold text-ink-900">Send via SMTP</h3>
            <p class="mt-2 text-sm text-ink-600">Plug in your SMTP credentials and email prospects directly from the report. Your domain, your reputation.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-28">
      <div class="container-tight">
        <div class="card p-10 lg:p-14 text-center bg-ink-900 border border-ink-800 relative overflow-hidden">
          <div class="absolute inset-0 bg-dot-ink bg-dot-20 opacity-[0.05]"></div>
          <div class="absolute -top-20 -right-20 w-72 h-72 bg-brand-500/20 rounded-full blur-3xl"></div>
          <div class="relative">
            <h2 class="font-display text-4xl text-white">Find your first warm lead today.</h2>
            <p class="mt-3 text-ink-300 max-w-lg mx-auto">Run a free audit, discover who needs your help, and start closing — no credit card.</p>
            <div class="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <router-link to="/register" class="btn-brand btn-lg">Get started free <Icon name="arrow-right" :size="16" /></router-link>
              <router-link to="/pricing" class="btn btn-lg bg-white/10 text-white border border-white/20 hover:bg-white/20">Compare plans</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { usePageMeta } from '../../composables/usePageMeta.js';
import Icon from '../ui/Icon.vue';

usePageMeta({
  title: 'Features — LeadRanks',
  meta: [
    { name: 'description', content: 'Find warm leads, run deep SEO + GEO audits, white-label everything, and export to your outreach stack. The only platform agencies and entrepreneurs need.' },
    { property: 'og:title', content: 'Features — LeadRanks' },
    { property: 'og:description', content: 'Find warm leads, run deep SEO + GEO audits, white-label everything, and export to your outreach stack. The only platform agencies and entrepreneurs need.' },
    { property: 'og:image', content: 'https://leadranks.io/og-image-1200x627.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ],
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://leadranks.io/' },
        { '@type': 'ListItem', position: 2, name: 'Features', item: 'https://leadranks.io/features' },
      ],
    },
  ],
});

// Basic vs Advanced — CLAUDE_TASKS.md Task 17. Kept short and non-overlapping
// with the feature groups above (competitor gap, GEO/AI citation, E-E-A-T,
// semantic coverage, and intent fit are already introduced there in more
// depth) — this list exists to answer the tier question plainly: what's on
// every plan vs. what Pro unlocks.
const basicAnalysis = [
  'Technical SEO audit — meta tags, headings, structure, schema',
  'Content analysis — readability, word count, keyword density',
  'Performance metrics — PageSpeed and Core Web Vitals',
  'Broken link checker',
  'Mobile responsiveness check',
];
const advancedAnalysis = [
  'Competitor gap analysis — keywords they rank for that you don\'t',
  'GEO score — AI-search visibility across ChatGPT, Perplexity, and AI Overviews',
  'Share of Voice tracking across the SERP',
  'AI-powered recommendations with specific, ready-to-send fixes',
  'AI Citation report — how AI models reference your brand',
  'Cannibalization detection',
  'E-E-A-T score',
  'Semantic coverage analysis',
  'Intent fit scoring',
];
const competitiveEdge = [
  { icon: 'bar-chart', title: 'Scored, not just listed', body: 'Every prospect gets a full SEO audit score before you ever reach out — you know who needs help, not just who exists.' },
  { icon: 'zap',       title: 'Warm by definition',      body: 'A low score means real, active pain — lost traffic and revenue. That\'s the opener a directory or cold list can\'t give you.' },
  { icon: 'sparkles',  title: 'AI insights in seconds',   body: 'Complete audit with actionable recommendations in seconds, not the hours a manual review takes.' },
  { icon: 'clock',     title: 'One workflow, not five tools', body: 'Find → analyze → score → report, in one pipeline — instead of juggling a directory, a checker, and a spreadsheet.' },
];

const groups = [
  {
    label: 'Find Warm Leads', icon: 'target', slug: 'find-warm-leads',
    title: 'Stop cold-calling. Start with sites that already need you.',
    description: 'LeadRanks scans the web for sites with broken SEO — missing titles, slow load times, no SSL, weak backlinks. These are warm leads who need exactly what you sell. Filter by niche, location, or keyword and build a pipeline in minutes.',
    features: [
      { icon: 'search',    title: 'Issue-based discovery',  body: 'Find sites with specific SEO problems — not random lists. Every lead has a demonstrable need.' },
      { icon: 'sliders',   title: 'Filter by niche & region', body: 'Target local businesses, e-commerce stores, SaaS — wherever your sweet spot is.' },
      { icon: 'bar-chart', title: 'Scored & prioritized',   body: 'Each lead comes with an SEO score and issue breakdown. Pitch the fix, not the feature.' },
      { icon: 'download',  title: 'One-click CSV export',   body: 'Pull your lead list as a CSV. Import directly into Instantly, Lemlist, or your CRM.' },
    ],
  },
  {
    label: 'Deep SEO Analysis', icon: 'code', slug: 'deep-seo-analysis',
    title: 'The audit your competitors wish didn\'t exist.',
    description: 'Not a surface-level checklist. LeadRanks runs parallel modules covering technical SEO, content quality, backlink authority, page speed, and more — then ranks every fix by revenue impact so you know exactly what to do first.',
    linkTo: '/seo-optimization',
    linkLabel: 'See the full SEO deep dive',
    features: [
      { icon: 'layers',   title: '17 parallel modules',     slug: 'technical-site-issues', body: 'Crawl health, structured data, security headers, mobile rendering — all at once, not one at a time.' },
      { icon: 'book',     title: 'Content quality scoring',  slug: 'content-quality-scoring', body: 'Readability, keyword density, heading hierarchy, semantic coverage. Know if your content actually answers the query.' },
      { icon: 'link',     title: 'Backlink & authority',     slug: 'backlink-authority', body: 'Referring domains, anchor distribution, toxic link detection. See who links to you — and who links to them.' },
      { icon: 'shield',   title: 'Competitor gap analysis',   slug: 'competitor-gap-analysis', body: 'Up to 3 competitors audited side-by-side. Keywords they rank for that you don\'t. Gaps you can close today.' },
    ],
  },
  {
    label: 'Performance & Speed', icon: 'zap', slug: 'performance-speed',
    title: 'Speed wins. Prove it.',
    description: 'Core Web Vitals, Lighthouse scores, image optimization — measured on mobile and desktop. Every metric translated into a fix your client (or dev team) can act on.',
    features: [
      { icon: 'trending-up', title: 'Core Web Vitals',      body: 'LCP, FCP, CLS, TBT — every metric Google uses, with clear pass/fail thresholds.' },
      { icon: 'zap',         title: 'PageSpeed Insights',    body: 'Lab data + real-user field data (CrUX). Mobile and desktop, side by side.' },
      { icon: 'image',       title: 'Image optimization',    body: 'Format (WebP/AVIF), compression, lazy loading, alt text, responsive sources.' },
      { icon: 'activity',    title: 'Revenue impact scoring', body: 'Every performance issue tagged with estimated traffic and revenue cost. Fix what pays.' },
    ],
  },
  {
    label: 'GEO & AI Readiness', icon: 'cpu', slug: 'geo-ai-readiness',
    title: 'Rank where it matters next: AI engines.',
    description: 'Google isn\'t the only game anymore. ChatGPT, Perplexity, and AI Overviews are citing sources — make sure yours is one of them. LeadRanks checks your AI-citability across every major engine.',
    linkTo: '/geo-optimization',
    linkLabel: 'See the full GEO deep dive',
    features: [
      { icon: 'cpu',       title: 'AI citation audit',       body: 'See if ChatGPT, Perplexity, and Google AI Overviews cite your content. Track your share of voice against competitors.' },
      { icon: 'check-circle', title: 'E-E-A-T scoring',      body: 'Experience, Expertise, Authoritativeness, Trustworthiness — the signals AI engines use to pick sources.' },
      { icon: 'sparkles',  title: 'Semantic coverage',       body: 'Does your content cover what AI engines expect? Topic depth, entity coverage, related concepts.' },
      { icon: 'bar-chart', title: 'Intent fit analysis',      body: 'Match your content to the actual intent behind each query. Informational, commercial, transactional — get it right.' },
    ],
  },
  {
    label: 'Action & Workflow', icon: 'tip', slug: 'action-workflow',
    title: 'Reports that do the work, not just show it.',
    description: 'Every finding comes with a ranked action plan. Copy-paste fixes, revenue projections, and one-click sharing. Go from audit to invoice in the same session.',
    features: [
      { icon: 'target',   title: 'Prioritized battle plan', body: 'Every issue ranked by impact × effort. Quick wins at the top. Revenue-anchored so you can justify the invoice.' },
      { icon: 'sparkles', title: 'AI copilot',             body: 'Ask your report questions. "What should I fix first?" "How much traffic am I losing?" Grounded on your actual data.' },
      { icon: 'share',    title: 'Share & send reports',    body: 'One-click share links. Email reports directly from the platform. Client-ready PDFs with your branding.' },
      { icon: 'clock',    title: 'Historical tracking',     body: 'Re-scan and compare. Show your client exactly how much their score improved since you started.' },
    ],
  },
];
</script>
